import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as tagsApi from '@/api/tags';
import type { ListTagsParams } from '@/api/tags';
import type {
  AssignLabelInput,
  Condition,
  CreateTagInput,
  UpdateTagInput,
} from '@/types/tags';

export function useTags(params?: ListTagsParams) {
  return useQuery({
    queryKey: ['tags', 'list', params],
    queryFn: () => tagsApi.listTags(params),
  });
}

export function useTag(id: string) {
  return useQuery({
    queryKey: ['tags', 'detail', id],
    queryFn: () => tagsApi.getTag(id),
    enabled: !!id,
  });
}

export function useUserTags(userId: string) {
  return useQuery({
    queryKey: ['tags', 'user', userId],
    queryFn: () => tagsApi.getUserTags(userId),
    enabled: !!userId,
  });
}

export function useCreateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTagInput) => tagsApi.createTag(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function useUpdateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTagInput }) =>
      tagsApi.updateTag(id, input),
    onSuccess: (_tag, vars) => {
      qc.invalidateQueries({ queryKey: ['tags'] });
      qc.invalidateQueries({ queryKey: ['tags', 'detail', vars.id] });
    },
  });
}

export function useDeleteTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tagsApi.deleteTag(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function useAssignTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AssignLabelInput }) =>
      tagsApi.assignTag(id, input),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['tags'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'students'] });
      for (const uid of vars.input.user_ids) {
        qc.invalidateQueries({ queryKey: ['tags', 'user', uid] });
      }
    },
  });
}

export function useUnassignTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AssignLabelInput }) =>
      tagsApi.unassignTag(id, input),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['tags'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'students'] });
      for (const uid of vars.input.user_ids) {
        qc.invalidateQueries({ queryKey: ['tags', 'user', uid] });
      }
    },
  });
}

export function useEvaluatePreview(rule: Condition | null) {
  return useQuery({
    queryKey: ['tags', 'preview', rule],
    queryFn: () => tagsApi.evaluatePreview(rule!),
    enabled: rule !== null,
  });
}

export function useTriggerSweep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => tagsApi.triggerSweep(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tags'] });
      qc.invalidateQueries({ queryKey: ['funnel'] });
    },
  });
}
